import React from 'react';
import {
  Badge as ChakraBadge,
  BadgeProps as ChakraBadgeProps,
  Box,
  useStyleConfig,
  IconProps,
  forwardRef,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { getVariantStyles, getBadgeIconStyle } from './badge.theme';

export type BadgeVariants = 'solid' | 'outline';
export type BadgeColorScheme = 'success' | 'alert' | 'warning' | 'info';

export interface BadgeProps extends ChakraBadgeProps {
  /**
   * A description of the badge for screen readers.
   */
  ariaLabel?: string;
  /**
   * The visual color appearance of the badge.
   * @default info
   */
  colorScheme?: BadgeColorScheme;
  /**
   * The color mode that the badge will display.
   * @default light
   */
  colorMode?: ColorMode;
  children?: React.ReactNode;
  /**
   * If added, the badge will show an icon before the badge's label.
   */
  icon?:
    | React.ReactElement<
        IconProps,
        string | React.JSXElementConstructor<IconProps>
      >
    | undefined;
  /**
   * The visual style of the badge.
   * @default solid
   */
  variant?: BadgeVariants;
}

/**
 * Badges are used to highlight an item's status for quick recognition.
 */
export const Badge = forwardRef<BadgeProps, 'span'>(
  (
    {
      ariaLabel,
      colorScheme,
      colorMode: propColorMode,
      children,
      icon,
      variant,
      ...otherProps
    },
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    const variantStyles = getVariantStyles(
      variant || 'solid',
      colorScheme || 'info',
      colorMode
    );
    const styles = useStyleConfig('Badge');
    const badgeStyles = { ...styles, ...variantStyles };

    return (
      <ChakraBadge
        ref={ref}
        role="status"
        aria-live="polite"
        aria-label={ariaLabel}
        sx={badgeStyles}
        {...otherProps}
      >
        {icon && (
          <Box aria-hidden="true" sx={getBadgeIconStyle()}>
            {icon}
          </Box>
        )}
        {children}
      </ChakraBadge>
    );
  }
);
